import React, { useCallback, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import SocialLoginSection from "../components/auth/SocialLoginSection";
import PrivacyPolicyConsentDialog from "../components/auth/PrivacyPolicyConsentDialog";
import MarketingThemeToggle from "../components/marketing/MarketingThemeToggle";
import { useAuth } from "../contexts/AuthContext";
import {
  getApiLanguage,
  VISIME_LANGUAGE_CHANGED,
  VISIME_UI_LANGUAGE_KEY,
} from "../config/apiLanguage";
import { registerUser } from "../services/api";
import { hasAcceptedPrivacyPolicy } from "../utils/privacyConsent";
import { useT } from "../i18n/useT";
import "./landing.css";
import "./guestSignup.css";

const ASSETS = `${process.env.PUBLIC_URL || ""}/marketing-assets`;
const LANGS: Array<"en" | "de"> = ["en", "de"];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[\d\W]).{8,30}$/;

export default function RegisterPage() {
  const t = useT();
  const lang = getApiLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [birthday, setBirthday] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submitRegistration = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await registerUser(
        email.trim(),
        password,
        firstName.trim(),
        birthday,
        1,
        getApiLanguage(),
      );

      if (response.message && !response.token && response.success === false) {
        throw new Error(response.message);
      }

      try {
        await login(email.trim(), password);
        navigate("/homepage");
      } catch {
        navigate("/?login=1", {
          state: {
            message:
              response.message ||
              "Account created. Please sign in with your email and password.",
          },
        });
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Registration failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [birthday, email, firstName, login, navigate, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (
      !email.trim() ||
      !password ||
      !confirmPassword ||
      !firstName.trim() ||
      !birthday
    ) {
      setError("Please fill in all fields");
      return;
    }

    if (!EMAIL_REGEX.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!PASSWORD_REGEX.test(password)) {
      setError(
        "Use 8–30 characters with upper, lower, and a number or special character",
      );
      return;
    }

    if (!hasAcceptedPrivacyPolicy()) {
      setPrivacyDialogOpen(true);
      return;
    }

    await submitRegistration();
  };

  const setLang = (code: "en" | "de") => {
    localStorage.setItem(VISIME_UI_LANGUAGE_KEY, code);
    window.dispatchEvent(new Event(VISIME_LANGUAGE_CHANGED));
  };

  return (
    <div className="landing-page gs-page">
      <div className="gs-grid">
        <aside className="gs-aside" aria-hidden="true">
          <div className="gs-aside-pin">
            <div className="l-stage gs-stage">
              <span className="l-stage-ring l-stage-ring--3" />
              <span className="l-stage-ring l-stage-ring--2" />
              <span className="l-stage-ring l-stage-ring--1" />
              <img
                className="l-stage-phone l-stage-phone--light"
                src={`${ASSETS}/Hero.png`}
                alt=""
              />
              <img
                className="l-stage-phone l-stage-phone--dark"
                src={`${ASSETS}/Hero-dark.png`}
                alt=""
              />
              <div className="l-stage-chip">
                <span className="l-dot" />
                <div>
                  <strong>{t("signupChipTitle")}</strong>
                  <span>{t("signupChipBody")}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <main className="gs-main">
          <header className="gs-bar">
            <RouterLink to="/?login=1" className="gs-back">
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              {t("signupBack")}
            </RouterLink>
            <div className="gs-tools">
              <div className="gs-lang" role="group" aria-label="Language">
                {LANGS.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className="gs-lang-btn"
                    aria-pressed={lang === code}
                    onClick={() => setLang(code)}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
              <MarketingThemeToggle />
            </div>
          </header>

          <div className="gs-card">
            <RouterLink to="/landing" className="gs-logo" aria-label="VisiMe">
              <img src={`${ASSETS}/VisiMe_Logo_RGB-1.png`} alt="" />
              <span>VisiMe</span>
            </RouterLink>
            <p className="l-eyebrow">{t("signupEyebrow")}</p>
            <h1 className="gs-title">{t("signupTitle")}</h1>
            <p className="gs-lead">{t("signupLead")}</p>

            {error && (
              <p className="gs-error" role="alert">
                {error}
              </p>
            )}

            <form className="gs-form" onSubmit={handleSubmit} noValidate>
              <div className="gs-field">
                <label htmlFor="gs-first-name">{t("registerFirstName")}</label>
                <input
                  id="gs-first-name"
                  className="gs-input"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="gs-field">
                <label htmlFor="gs-email">{t("email")}</label>
                <input
                  id="gs-email"
                  className="gs-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="gs-field">
                <label htmlFor="gs-password">{t("password")}</label>
                <input
                  id="gs-password"
                  className="gs-input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  aria-describedby="gs-password-hint"
                />
                <span id="gs-password-hint" className="gs-hint">
                  {t("signupPasswordHint")}
                </span>
              </div>
              <div className="gs-field">
                <label htmlFor="gs-confirm">
                  {t("registerConfirmPassword")}
                </label>
                <input
                  id="gs-confirm"
                  className="gs-input"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="gs-field">
                <label htmlFor="gs-birthday">{t("registerBirthday")}</label>
                <input
                  id="gs-birthday"
                  className="gs-input"
                  type="date"
                  autoComplete="bday"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <p className="gs-consent">
                {t("signupConsentPrefix")}{" "}
                <button
                  type="button"
                  className="gs-link"
                  onClick={() => setPrivacyDialogOpen(true)}
                >
                  {t("signupPrivacy")}
                </button>
                {t("signupConsentSuffix")}
              </p>

              <button
                type="submit"
                className="gs-submit"
                disabled={isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? t("loading") : t("signupSubmit")}
              </button>
            </form>

            <SocialLoginSection
              tone="marketing"
              disabled={isLoading}
              onSuccess={() => navigate("/homepage")}
              onError={(message) => setError(message)}
            />

            <p className="gs-switch">
              {t("signupHaveAccount")}{" "}
              <RouterLink to="/?login=1" className="gs-link">
                {t("signupSignIn")}
              </RouterLink>
            </p>
          </div>
        </main>
      </div>

      <PrivacyPolicyConsentDialog
        open={privacyDialogOpen}
        onClose={() => setPrivacyDialogOpen(false)}
        onAccepted={() => {
          setPrivacyDialogOpen(false);
          void submitRegistration();
        }}
      />
    </div>
  );
}
